/**
 * Cache en memoria para datos que se piden desde múltiples componentes.
 * Evita requests duplicados de asesores, empresas, etc.
 * TTL de 5 minutos - después de eso se refresca.
 */

import { userService } from '@/lib/api/userService';
import { empresaService } from '@/lib/api/empresaService';
import { User } from '@/types/user';
import { Empresa } from '@/types/empresa';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  promise?: Promise<T>;
}

const cache: Record<string, CacheEntry<any>> = {};

function isValid(key: string): boolean {
  const entry = cache[key];
  if (!entry) return false;
  return Date.now() - entry.timestamp < CACHE_TTL;
}

async function getCached<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  if (isValid(key)) return cache[key].data;

  // Si ya hay un request en vuelo, reutilizarlo (deduplicación)
  if (cache[key]?.promise) return cache[key].promise!;

  const promise = fetcher().then((data) => {
    cache[key] = { data, timestamp: Date.now() };
    return data;
  }).catch((err) => {
    delete cache[key];
    throw err;
  });

  cache[key] = { ...cache[key], promise, data: cache[key]?.data, timestamp: cache[key]?.timestamp ?? 0 };
  return promise;
}

export function invalidateCache(key?: string) {
  if (key) {
    delete cache[key];
  } else {
    Object.keys(cache).forEach((k) => delete cache[k]);
  }
}

export async function getAsesoresCached(): Promise<User[]> {
  return getCached('asesores', async () => {
    const resp = await userService.getUsersByAcl('asesor');
    return (resp.data || []).filter((u: User) => (u.userEstatus || '').toLowerCase() === 'activo');
  });
}

export async function getEmpresasCached(): Promise<Empresa[]> {
  return getCached('empresas', async () => {
    const resp = await empresaService.getAll();
    const empresas = resp.data || [];
    // Ordenar alfabéticamente por nombre
    return empresas.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
  });
}

/**
 * Obtiene TODOS los usuarios (no solo asesores).
 * Usado para el selector de Asesor Origen que puede ser cualquier usuario.
 */
export async function getAllUsersCached(): Promise<User[]> {
  return getCached('allUsers', async () => {
    const resp = await userService.getUsers();
    return (resp.data || []).filter((u: User) => (u.userEstatus || '').toLowerCase() === 'activo');
  });
}
